import { FloatingFormItem } from '@/components/_common';
import {
  useWorkOrderChecklistRequestServiceCreate,
  useWorkOrderChecklistRequestServiceUpdate,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import {
  WorkOrderChecklistRequestService,
  WorkOrderChecklistRequestServiceFormValues,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Form, Input, Modal, ModalProps, Select } from 'antd';
import { useWorkorderChecklistContext } from '../WorkorderChecklistProvider';
import { WorkorderChecklistItemType } from '../WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import { isAppPortocar } from '@/index';
import { useCallback, useEffect } from 'react';

interface Props extends ModalProps {
  onCancel: () => void;
  requestService?: WorkOrderChecklistRequestService;
}

const services = [
  'Alinhamento/Balanceamento',
  'Freios',
  'Suspensão/Direção',
  'Pneus',
  'Transmissão',
  'Lubrificantes/Filtros',
  'Injeção eletrónica/Ignição',
  'Arrefecimento',
  'Motor/Exaustão',
];

const serviceOptions = [
  ...services.map((service) => ({ value: service, label: service })),
  { value: 'other', label: 'Outro' },
];

const WorkorderChecklistRequestServiceCreateModal: React.FC<Props> = (
  props,
) => {
  const [form] = Form.useForm<WorkOrderChecklistRequestServiceFormValues>();
  const { checklistItemId, checklistItemType } = useWorkorderChecklistContext();
  const requestServiceCreate = useWorkOrderChecklistRequestServiceCreate();
  const requestServiceUpdate = useWorkOrderChecklistRequestServiceUpdate();

  const isEdit = !!props.requestService;

  const serviceSelected = Form.useWatch('requestedServiceSelected', form);
  const hasOther = serviceSelected === 'other';

  const ruleRequired = useDefaultEmptyFieldError();

  useEffect(() => {
    if (props.requestService) {
      form.setFieldsValue({
        comments: props.requestService.comments,
        requestedService: props.requestService.requestedService,
      });
    }
  }, [props.requestService]);

  const isLoading =
    requestServiceCreate.isLoading || requestServiceUpdate.isLoading;

  const handleCancel = useCallback(() => {
    if (isLoading) return;

    form.resetFields();
    props.onCancel();
  }, [isLoading]);

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      const payload = {
        comments: values.comments,
        requestedService: values.requestedService,
      };

      if (props.requestService) {
        await requestServiceUpdate.mutateAsync({
          ...payload,
          id: props.requestService.id,
        });
      } else if (checklistItemType === WorkorderChecklistItemType.Workorder) {
        await requestServiceCreate.mutateAsync({
          ...payload,
          workOrderComplementId: checklistItemId,
        });
      } else if (checklistItemType === WorkorderChecklistItemType.Diagnostic) {
        await requestServiceCreate.mutateAsync({
          ...payload,
          diagnosticId: checklistItemId,
        });
      }
      handleCancel();
    } catch {}
  }

  return (
    <Modal
      {...props}
      title={
        isEdit
          ? 'Editar solicitação de serviço'
          : 'Cadastrar solicitação de serviço'
      }
      width={500}
      onCancel={handleCancel}
      okText="Salvar"
      onOk={handleSubmit}
      confirmLoading={isLoading}
    >
      <Form form={form}>
        {isAppPortocar && (
          <FloatingFormItem
            rules={[ruleRequired]}
            label="Selecione um serviço"
            name="requestedServiceSelected"
            getValueFromEvent={(value) => {
              if (value === 'other') {
                form.setFieldValue('requestedService', undefined);
              } else {
                form.setFieldValue('requestedService', value);
              }
              return value;
            }}
          >
            <Select options={serviceOptions} disabled={isLoading} />
          </FloatingFormItem>
        )}

        {!hasOther && isAppPortocar && (
          <Form.Item name="requestedService" hidden />
        )}

        {(hasOther || !isAppPortocar) && (
          <FloatingFormItem
            rules={[ruleRequired]}
            label="Descrição do serviço"
            name="requestedService"
          >
            <Input disabled={isLoading} />
          </FloatingFormItem>
        )}

        <FloatingFormItem
          rules={[
            { type: 'string', max: 500, message: 'Máximo de 500 caracteres' },
          ]}
          label="Observação"
          name="comments"
        >
          <Input.TextArea rows={4} disabled={isLoading} />
        </FloatingFormItem>
      </Form>
    </Modal>
  );
};

export default WorkorderChecklistRequestServiceCreateModal;
